import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ParallaxSection } from './ParallaxSection';
import { RevealWrapper } from '../hooks/useReveal';
import { Shield, Zap, Cloud, Lock, Sparkles, Rocket } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

export const Hero = () => {
  // Wolkenheld mascot images for light and dark mode
  const mascotLightUrl = '/images/held_light.png';
  const mascotDarkUrl = '/images/held_dark.png';
  
  const [basePrice, setBasePrice] = useState(null);

  useEffect(() => {
    fetch(`${backendUrl}/api/config/pricing`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBasePrice(data.base_price); })
      .catch(() => {});
  }, []);

  const priceDisplay = basePrice ? basePrice.toFixed(2).replace('.', ',') : '9,00';

  // Track dark mode state
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    // Check initial dark mode state
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    // Initial check
    checkDarkMode();
    
    // Watch for theme changes using MutationObserver
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  
  const mascotUrl = isDarkMode ? mascotDarkUrl : mascotLightUrl;

  return (
    <section
      className="relative min-h-[72vh] overflow-hidden bg-gradient-to-b from-[hsl(206,100%,97%)] via-[hsl(205,92%,95%)] to-[hsl(210,50%,98%)] dark:from-[hsl(222,34%,7%)] dark:via-[hsl(222,28%,10%)] dark:to-[hsl(222,34%,7%)] bg-noise-soft"
      data-testid="hero-section"
    >
      <div className="container relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:grid lg:grid-cols-12 lg:gap-8 lg:px-8">
        {/* Left: Copy */}
        <div className="lg:col-span-7 lg:py-16">
          <RevealWrapper>
            <Badge
              variant="secondary"
              className="mb-4 text-xs bg-gradient-to-r from-primary/20 to-blue-500/20 border-primary/30"
              data-testid="hero-badge"
            >
              🦸 Managed Nextcloud Hosting für echte Helden
            </Badge>
          </RevealWrapper>

          <RevealWrapper delay={100}>
            <h1
              className="font-display text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="hero-h1"
            >
              Deine Nextcloud.
              <br />
              <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                Von Helden betrieben.
              </span>
            </h1>
          </RevealWrapper>

          <RevealWrapper delay={200}>
            <p
              className="mt-6 text-lg text-muted-foreground md:text-xl"
              data-testid="hero-description"
            >
              Du willst eine Cloud, die einfach läuft? Wir liefern dir eine
              <strong> heldenhafte Managed Nextcloud</strong> auf hochverfügbarer Infrastruktur mit S3-Storage –
              blitzschnell ⚡, bombensicher 🛡️ und selbstheilend 💪.
            </p>
          </RevealWrapper>

          <RevealWrapper delay={300}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                size="lg"
                asChild
                className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 transition-all hover:scale-105 shadow-lg shadow-primary/25"
                data-testid="hero-primary-cta-button"
              >
                <Link to="/preise" className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Werde zum Cloud-Helden!
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="border-primary/30 hover:bg-primary/5"
                data-testid="hero-secondary-cta-button"
              >
                <Link to="/technik" className="flex items-center gap-2">
                  <Rocket className="h-5 w-5" />
                  Superkräfte entdecken
                </Link>
              </Button>
            </div>
          </RevealWrapper>

          {/* Trust Badges */}
          <RevealWrapper delay={400}>
            <div className="mt-12 flex flex-wrap gap-3">
              {[
                { icon: Shield, text: 'Helden-Verfügbarkeit', emoji: '🛡️' },
                { icon: Zap, text: 'Selbstheilend', emoji: '⚡' },
                { icon: Cloud, text: 'Helden-Speicher', emoji: '☁️' },
                { icon: Lock, text: 'DSGVO-Held', emoji: '🔒' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-full bg-white/60 px-4 py-2 text-sm font-medium dark:bg-white/5 border border-primary/10 hover:border-primary/30 transition-colors"
                  data-testid={`trust-badge-${i}`}
                >
                  <span>{item.emoji}</span>
                  {item.text}
                </div>
              ))}
            </div>
          </RevealWrapper>

          {/* Price teaser */}
          <RevealWrapper delay={500}>
            <div className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/20 px-4 py-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-sm">
                Ab <span className="font-bold text-primary">{priceDisplay} €</span>/Monat • 
                <span className="text-muted-foreground ml-1">Heldenhafte 250 GB inklusive!</span>
              </span>
            </div>
          </RevealWrapper>
        </div>

        {/* Right: Mascot with Parallax */}
        <div className="relative mt-12 lg:col-span-5 lg:mt-0">
          <ParallaxSection speed={0.08} className="relative">
            <div
              className="relative mx-auto max-w-md"
              data-testid="hero-mascot"
            >
              <div className="animate-float">
                <img
                  src={mascotUrl}
                  alt="Wolkenheld Maskottchen - dein fliegender Cloud-Superheld"
                  className="w-full drop-shadow-2xl"
                  style={{ mixBlendMode: 'normal' }}
                  loading="eager"
                />
              </div>
              {/* Glow effect */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/20 to-blue-500/20 blur-3xl rounded-full scale-75" />
            </div>
          </ParallaxSection>
        </div>
      </div>

      {/* Decorative gradient blobs */}
      <div className="absolute right-0 top-0 -z-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute bottom-0 left-0 -z-10 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
    </section>
  );
};
